import { useLocation } from "wouter";
import { useChatHalfHeight } from "@/hooks/use-chat-half-height";
import { useChatNarrowWidth } from "@/hooks/use-chat-narrow-width";
import { Header } from "@/components/Header";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { SmartAIChat } from "@/components/SmartAIChat";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Edit, RefreshCw } from "lucide-react";
import { useTest } from "@/context/TestContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ReviewBlueprint() {
  const [, setLocation] = useLocation();
  const { blueprint, questions, setQuestions } = useTest();
  // Desktop sidebar assistant: chevron toggles between full-viewport and
  // half-viewport height. Shared store so toggling here persists when
  // the user moves to Build Blueprint or any other page that uses it.
  const [assistantHalfHeight] = useChatHalfHeight();
  const [assistantNarrow] = useChatNarrowWidth();

  if (!blueprint) {
    setLocation("/create/blueprint");
    return null;
  }

  const totalItems = blueprint.topics.reduce(
    (sum, topic) => sum + topic.knowledge + topic.understanding + topic.higherOrderThinking,
    0
  );
  const totalKnowledge = blueprint.topics.reduce((sum, topic) => sum + topic.knowledge, 0);
  const totalUnderstanding = blueprint.topics.reduce((sum, topic) => sum + topic.understanding, 0);
  const totalHigherOrder = blueprint.topics.reduce((sum, topic) => sum + topic.higherOrderThinking, 0);

  const handleEdit = () => {
    setLocation("/create/blueprint");
  };

  const handleGenerate = () => {
    setLocation("/create/generate");
  };

  // Throw away existing questions and route to the generate page so
  // Blacksmith produces a fresh set from the current blueprint.
  const handleRegenerateTest = () => {
    setQuestions([]);
    setLocation("/create/generate");
  };

  const handleBack = () => {
    setLocation("/create/blueprint");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header pageTitle="Create Test" />

      <main className="container mx-auto px-6 py-12 max-w-7xl">
        <ProgressIndicator currentStep={3} />

        <div
          className={`grid grid-cols-1 ${assistantNarrow ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-6`}
        >
          <div className={assistantNarrow ? "lg:col-span-3" : "lg:col-span-2"}>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Accept and Approve Your Test Blueprint
              </h1>
              <p className="text-muted-foreground">
                Review your blueprint before generating questions
              </p>
            </div>

            <div className="space-y-4 mb-8">
              {blueprint.topics.map((topic) => (
                <Card key={topic.id} className="border-l-4 border-l-primary" data-testid={`topic-${topic.id}`}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold">{topic.name}</h3>
                        <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full" data-testid={`blueprint-percentage-${topic.id}`}>
                          {topic.percentage}% of test
                        </span>
                      </div>

                      <div className="flex justify-between gap-2 text-base ml-24 items-end">
                        <div className="text-center">
                          <p className="text-muted-foreground text-sm mb-1">Knowledge</p>
                          <p className="text-lg font-semibold" data-testid={`blueprint-knowledge-${topic.id}`}>{topic.knowledge} items</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground text-sm mb-1">Understanding</p>
                          <p className="text-lg font-semibold" data-testid={`blueprint-understanding-${topic.id}`}>{topic.understanding} items</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground text-sm leading-tight mb-1">Application+</p>
                          <p className="text-lg font-semibold" data-testid={`blueprint-higher-order-${topic.id}`}>{topic.higherOrderThinking} items</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground text-sm mb-1">Total</p>
                          <p className="text-lg font-semibold" data-testid={`blueprint-total-${topic.id}`}>
                            {topic.knowledge + topic.understanding + topic.higherOrderThinking} items
                          </p>
                        </div>
                      </div>

                      {(topic.standards || []).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <h4 className="text-sm font-semibold text-muted-foreground mb-2">Standards</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs text-muted-foreground">
                                  <th className="text-left font-medium py-1 pr-2">Code</th>
                                  <th className="text-left font-medium py-1 px-2">Description</th>
                                  <th className="text-center font-medium py-1 px-2 w-16">K</th>
                                  <th className="text-center font-medium py-1 px-2 w-16">U</th>
                                  <th className="text-center font-medium py-1 px-2 w-24">Application+</th>
                                  <th className="text-center font-medium py-1 pl-2 w-16">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(topic.standards || []).map((std) => {
                                  const subtotal = (std.knowledge || 0) + (std.understanding || 0) + (std.higherOrderThinking || 0);
                                  return (
                                    <tr key={std.code} className="border-t border-border/30">
                                      <td className="py-1.5 pr-2 font-medium">{std.code}</td>
                                      <td className="py-1.5 px-2 text-muted-foreground">{std.description}</td>
                                      <td className="py-1.5 px-2 text-center">{std.knowledge ?? 0}</td>
                                      <td className="py-1.5 px-2 text-center">{std.understanding ?? 0}</td>
                                      <td className="py-1.5 px-2 text-center">{std.higherOrderThinking ?? 0}</td>
                                      <td className="py-1.5 pl-2 text-center font-semibold">{subtotal}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-primary/5 border-primary/20 mb-8">
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-4">Test Summary</h3>
                <div className="flex flex-wrap gap-8 md:gap-12">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Items</p>
                    <p className="text-2xl font-bold text-primary" data-testid="text-total-items">{totalItems}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Knowledge</p>
                    <p className="text-2xl font-bold text-primary">{totalKnowledge}</p>
                    <p className="text-xs text-muted-foreground">{Math.round((totalKnowledge / totalItems) * 100)}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Understanding</p>
                    <p className="text-2xl font-bold text-primary">{totalUnderstanding}</p>
                    <p className="text-xs text-muted-foreground">{Math.round((totalUnderstanding / totalItems) * 100)}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Application+</p>
                    <p className="text-2xl font-bold text-primary">{totalHigherOrder}</p>
                    <p className="text-xs text-muted-foreground">{Math.round((totalHigherOrder / totalItems) * 100)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleBack} data-testid="button-back">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex gap-3 flex-wrap">
                <Button variant="secondary" onClick={handleEdit} data-testid="button-edit">
                  Edit Blueprint <Edit className="w-4 h-4" />
                </Button>
                {questions && questions.length > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="secondary" data-testid="button-regenerate-test">
                        Regenerate Test <RefreshCw className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Regenerate the test from this blueprint?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will discard the {questions.length} existing question{questions.length === 1 ? '' : 's'} (including any edits, locks, and regeneration history) and ask Blacksmith for a fresh set based on the current blueprint. Your blueprint itself stays as is.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRegenerateTest}>
                          Regenerate Test
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <Button onClick={handleGenerate} data-testid="button-generate">
                  {questions && questions.length > 0 ? "Review Items" : "Generate Test"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div
              className={`sticky top-6 ${assistantHalfHeight ? "h-[70vh]" : "h-[calc(100vh-3rem)]"}`}
            >
              <SmartAIChat
                context="review-blueprint"
                className="h-full"
                toggleMode="half"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
